"use client";

import type { LocalStorageSyncOptions } from "@noya-app/state-manager";
import {
  NoyaManager,
  NoyaManagerOptions,
  type SyncAdapter,
} from "@noya-app/state-manager";
import React from "react";
import type { StateInspectorOptions } from "./inspector/StateInspector";
import { StateInspector } from "./inspector/StateInspector";
import { createStateInspectorRoot } from "./inspector/useStateInspector";
import {
  type AppTheme,
  type AppViewType,
  getAppData,
  getSyncAdapter,
} from "./noyaApp";

let noyaManagerSingleton: NoyaManager<any, any, any, any, any> | undefined;
let noyaSingletonBindings: AnyNoyaSingletonBindings | undefined;

export type AnyNoyaSingletonBindings = {
  sync?: SyncAdapter<any, any, any, any, any>;
  inspector?: boolean | StateInspectorOptions;
  theme: AppTheme;
  viewType: AppViewType;
  userId?: string;
  isDemo: boolean;
};

export type AnyNoyaSingletonOptions<
  S,
  M extends object,
  E extends object,
  MenuT extends string,
  I extends Record<string, any> = Record<string, unknown>,
> = NoyaManagerOptions<S, M> & {
  initialState?: S | (() => S);
  sync?: SyncAdapter<S, M, E, MenuT, I>;
  inspector?: boolean | StateInspectorOptions;
  theme?: AppTheme;
  viewType?: AppViewType;
  offlineStorageKey?: string;
  multiplayerUrl?: string;
  serverScripts?: LocalStorageSyncOptions["serverScripts"];
  git?: LocalStorageSyncOptions["git"];
};

export function initializeNoyaSingleton<
  S,
  M extends object,
  E extends object,
  MenuT extends string,
  I extends Record<string, any> = Record<string, unknown>,
>(options?: AnyNoyaSingletonOptions<S, M, E, MenuT, I>) {
  if (noyaManagerSingleton) {
    throw new Error(
      "Noya has already been initialized. Call `useNoyaState` instead."
    );
  }

  const {
    initialState,
    sync,
    inspector,
    theme,
    viewType,
    offlineStorageKey,
    multiplayerUrl,
    ...managerOptions
  } = options ?? {};

  const appData = getAppData<S>(
    (initialState ?? null) as S | (() => S),
    managerOptions?.schema,
    { overrideExistingState: managerOptions?.overrideExistingState }
  );

  const policyAuthContext =
    appData.access || appData.userId !== undefined
      ? {
          ...(appData.access ? { access: appData.access } : {}),
          ...(appData.userId !== undefined
            ? { id: appData.userId ?? null }
            : {}),
        }
      : undefined;

  noyaManagerSingleton = new NoyaManager<S, M, E, MenuT, I>(
    appData.initialState as S,
    {
      ...managerOptions,
      resourceUrlBase:
        managerOptions.resourceUrlBase ??
        appData.parentOrigin ??
        (typeof globalThis.location === "undefined"
          ? undefined
          : globalThis.location.origin === "null"
            ? undefined
            : globalThis.location.origin),
    }
  );

  if (appData.userId) {
    noyaManagerSingleton.sharedConnectionDataManager.setCurrentConnectionId(
      appData.userId
    );
    noyaManagerSingleton.userManager.setCurrentConnectionId(appData.userId);
    noyaManagerSingleton.userManager.setCurrentUserId(appData.userId);
  }

  const resolvedSync =
    sync ??
    getSyncAdapter<S, M, E, MenuT, I>({
      multiplayerUrl: multiplayerUrl ?? appData.multiplayerUrl,
      offlineStorageKey,
      debug: managerOptions?.debug,
      serverScripts: managerOptions?.serverScripts,
      git: managerOptions?.git,
      policyAuthContext,
      parentOrigin: appData.parentOrigin,
      enableDocumentAI: appData.enableDocumentAI,
    });

  // Activate sync immediately for the singleton
  try {
    resolvedSync({ noyaManager: noyaManagerSingleton });
  } catch (error) {
    console.warn("Failed to activate sync", error);
  }

  const resolvedInspector = inspector ?? appData.inspector;
  const resolvedTheme = theme ?? appData.theme ?? "light";
  const resolvedViewType = viewType ?? appData.viewType ?? "editable";

  if (resolvedInspector) {
    const { root } = createStateInspectorRoot();

    root.render(
      <StateInspector
        noyaManager={noyaManagerSingleton}
        colorScheme={
          resolvedInspector === true
            ? resolvedTheme
            : resolvedInspector.colorScheme
        }
        anchor={
          resolvedInspector === true ? "bottom right" : resolvedInspector.anchor
        }
        advanced={
          resolvedInspector === true ? false : resolvedInspector.advanced
        }
        defaultCollapsed={
          resolvedInspector === true
            ? false
            : resolvedInspector.defaultCollapsed
        }
      />
    );
  }

  noyaSingletonBindings = {
    sync: resolvedSync,
    inspector: resolvedInspector,
    theme: resolvedTheme,
    viewType: resolvedViewType,
    userId: appData.userId,
    isDemo: appData.isDemo,
  };
}

export function getNoyaManagerSingleton(): NoyaManager<
  any,
  any,
  any,
  any,
  any
> {
  if (!noyaManagerSingleton || !noyaSingletonBindings) {
    throw new Error(
      "Noya has not been initialized. Call initializeNoya once before using Noya hooks."
    );
  }

  return noyaManagerSingleton;
}

export function getNoyaSingletonBindings() {
  return noyaSingletonBindings;
}

export function deleteNoyaSingleton() {
  noyaManagerSingleton = undefined;
  noyaSingletonBindings = undefined;
}
